import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  HardHat,
  Wrench,
  Factory,
  Building2,
  Drill,
  Settings,
  ShieldCheck,
  Activity,
  Award,
} from "lucide-react";
import { SectionHeading, SectionLabel } from "../components/SectionHeading";
import { useSite, absoluteUrl } from "../context/SiteContext";
import SmartImage from "../components/SmartImage";

const ICONS = { HardHat, Wrench, Factory, Building2, Drill, Settings };

export default function HomePage() {
  const { settings, services, projects } = useSite();
  const { brand, hero, stats } = settings;
  const featured = projects.filter((p) => p.featured).slice(0, 3);
  const showcase = featured.length ? featured : projects.slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#1F3550]">
        <div className="absolute inset-0">
          {hero.image && (
            <img
              src={absoluteUrl(hero.image)}
              alt="industrial construction"
              className="w-full h-full object-cover opacity-50"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-[#050A0F] via-[#050A0F]/85 to-[#050A0F]/30" />
          <div className="absolute inset-0 blueprint-grid opacity-50" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-32 md:py-44 lg:py-52">
          <SectionLabel code="01">{hero.eyebrow}</SectionLabel>
          <h1 className="mt-6 font-heading font-black text-5xl md:text-6xl lg:text-7xl tracking-tight uppercase leading-[0.95]">
            {hero.headline_top}
            <br />
            <span className="text-[#F39C12]">{hero.headline_accent}</span>{" "}
            {hero.headline_tail}
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-[#94A3B8] leading-relaxed">
            {hero.subtitle}
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link to="/contact" data-testid="hero-cta-quote" className="inline-flex items-center gap-2 bg-[#F39C12] text-[#050A0F] px-7 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#D68910] transition-colors">
              Request a Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/services" data-testid="hero-cta-services" className="inline-flex items-center gap-2 border border-[#F39C12] text-[#F39C12] px-7 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#F39C12]/10 transition-colors">
              Our Services
            </Link>
          </div>

          {stats.length > 0 && (
            <div className="mt-20 grid grid-cols-2 md:grid-cols-4 border-t border-[#1F3550]">
              {stats.map((s, i) => (
                <div key={i} data-testid={`stat-${i}`} className={`py-6 px-2 ${i !== 0 ? "md:border-l border-[#1F3550]" : ""}`}>
                  <div className="font-heading font-black text-3xl md:text-4xl text-[#F39C12]">{s.value}</div>
                  <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#94A3B8]">{s.label}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-b border-[#1F3550]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <SectionHeading
              code="02"
              overline="Capabilities"
              title="Six Disciplines. One Accountable Team."
              subtitle="From planning and fabrication to commissioning and handover — we own every meter of weld and every micron of alignment."
            />
            <Link to="/services" className="hidden md:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#F39C12] hover:text-[#D68910]">
              View All Services <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F3550] border border-[#1F3550]">
            {services.map((s) => {
              const Icon = ICONS[s.icon] || Wrench;
              return (
                <Link to="/services" key={s.id} data-testid={`service-card-${s.id}`} className="group relative bg-[#050A0F] p-8 hover:bg-[#0D1B2A] transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="font-mono text-[10px] tracking-[0.3em] text-[#F39C12]">{s.code}</div>
                    <Icon className="h-7 w-7 text-[#F39C12]" />
                  </div>
                  <h3 className="mt-8 font-heading font-bold text-xl text-[#F8FAFC] leading-tight">{s.title}</h3>
                  <p className="mt-3 text-sm text-[#94A3B8] leading-relaxed">{s.short}</p>
                  <div className="mt-8 flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#F39C12] opacity-0 group-hover:opacity-100 transition-opacity">
                    Learn More <ArrowRight className="h-3 w-3" />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="border-b border-[#1F3550]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              code="03"
              overline="Why TST & MK"
              title="Performance is the only certificate that matters."
              subtitle="We earn renewed orders by walking the talk on every project. Safety, schedule and quality — non-negotiable."
            />
            <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#F39C12] hover:text-[#D68910]">
              Read Our Story <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#1F3550] border border-[#1F3550]">
            {[
              { icon: ShieldCheck, title: "Zero-Compromise Safety", text: "HSE-first culture audited at every level." },
              { icon: Activity, title: "Schedule Integrity", text: "We deliver on the date we signed." },
              { icon: Award, title: "Award-Winning Quality", text: "Recognized by India's top industrial clients." },
              { icon: Factory, title: "End-to-End Capability", text: "From concept to commissioning under one team." },
            ].map((b, i) => (
              <div key={i} className="bg-[#050A0F] p-8">
                <b.icon className="h-6 w-6 text-[#F39C12]" />
                <h4 className="mt-6 font-heading font-bold text-lg text-[#F8FAFC]">{b.title}</h4>
                <p className="mt-2 text-sm text-[#94A3B8] leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RECENT PROJECTS */}
      <section className="border-b border-[#1F3550]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <SectionHeading code="04" overline="Selected Work" title="Projects Delivered Under Pressure." subtitle="A selection from the shutdowns, builds and erections we've executed across heavy industries." />
            <Link to="/projects" className="hidden md:inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#F39C12] hover:text-[#D68910]">
              All Projects <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {showcase.map((p) => (
              <Link to="/projects" key={p.id} data-testid={`featured-project-${p.id}`} className="group block border border-[#1F3550] bg-[#0D1B2A] hover:border-[#F39C12] transition-colors">
                <div className="relative aspect-[4/3] overflow-hidden">
                  <SmartImage src={p.image} alt={p.title} imgClassName="grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050A0F] via-transparent to-transparent" />
                  {p.code && (
                    <div className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.25em] text-[#F39C12] bg-[#050A0F]/80 px-2 py-1">{p.code}</div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.25em] uppercase text-[#94A3B8]">
                    <span>{p.sector}</span>
                    <span className="h-px w-4 bg-[#3A506B]" />
                    <span>{p.year}</span>
                  </div>
                  <h3 className="mt-3 font-heading font-bold text-lg text-[#F8FAFC] leading-tight">{p.title}</h3>
                </div>
              </Link>
            ))}
            {showcase.length === 0 && (
              <div className="col-span-full text-center py-16 text-[#94A3B8] border border-dashed border-[#1F3550]">
                Projects coming soon.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#F39C12] text-[#050A0F] relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-2 stripe-accent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase">Let's Build</div>
            <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl uppercase tracking-tight">Have a project on the table?</h2>
            <p className="mt-3 text-base md:text-lg max-w-xl">Send the scope. We'll send back a plan, a price and a date we'll honour.</p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/contact" data-testid="cta-bottom" className="inline-flex items-center gap-2 bg-[#050A0F] text-[#F39C12] px-8 py-4 font-bold uppercase tracking-widest text-sm hover:bg-[#0D1B2A] transition-colors">
              Start a Project <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
